import React, { useState, useEffect } from 'react';
import { Boxes, AlertTriangle, CheckCircle2, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { productService } from '../services/api.service';
import { getProductImageUrl } from '../../services/api';

const ITEMS_PER_PAGE = 10;
const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || "http://103.99.202.191:6020";

const Inventory = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [stockInputs, setStockInputs] = useState({});
  const [successMsg, setSuccessMsg] = useState('');

  const defaultImg = `${BACKEND_BASE}/uploads/product-pro.png`;

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const res = await productService.getProducts();
      const items = res.products || [];
      setProducts(items);

      const initialInputs = {};
      items.forEach(p => {
        initialInputs[p.id] = p.stock;
      });
      setStockInputs(initialInputs);
    } catch (err) {
      console.error('Failed to load inventory:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
  }, []);

  const handleStockChange = (id, value) => {
    const clampedVal = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0);
    setStockInputs({ ...stockInputs, [id]: clampedVal });
  };

  const handleSaveStock = async (id) => {
    const rawVal = stockInputs[id];
    const newStock = Math.max(0, parseInt(rawVal, 10) || 0);
    if (isNaN(newStock) || newStock < 0) return;

    try {
      await productService.updateStock(id, newStock);
      setSuccessMsg(`Stock level updated successfully.`);
      fetchInventory();
    } catch (err) {
      alert('Failed to update stock.');
    }
  };

  const lowStockItems = products.filter(p => p.stock <= 18);

  // Pagination Calculations
  const totalPages = Math.ceil(products.length / ITEMS_PER_PAGE) || 1;
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentProducts = products.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Page Header */}
      <div>
        <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>Inventory & Stock Radar</h2>
        <p style={{ fontSize: '13px', color: '#64748b' }}>Monitor real-time Red Light Therapy panel warehouse quantities and reorder thresholds.</p>
      </div>

      {/* Low Stock Critical Alert Banner */}
      {lowStockItems.length > 0 && (
        <div className="form-alert" style={{ background: '#fff7ed', borderColor: '#fdba74', color: '#c2410c' }}>
          <AlertTriangle size={18} />
          <span>
            <strong>Inventory Alert:</strong> {lowStockItems.length} Panel model(s) are below the recommended reorder threshold (18 units).
          </span>
        </div>
      )}

      {successMsg && (
        <div className="form-alert" style={{ background: '#ecfdf5', borderColor: '#a7f3d0', color: '#047857' }}>
          <CheckCircle2 size={16} />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Inventory Table */}
      <div className="table-card-box">
        <div className="card-header-flex">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Boxes size={20} color="#dc2626" />
            <h3 className="card-title-text">Warehouse Stock Levels</h3>
          </div>
          <button onClick={fetchInventory} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
            <RefreshCw size={14} />
            <span>Refresh</span>
          </button>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading warehouse inventory...</div>
        ) : (
          <>
            <table className="custom-data-table">
              <thead>
                <tr>
                  <th>Panel Model</th>
                  <th>SKU</th>
                  <th>Current Stock</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Update Quantity</th>
                </tr>
              </thead>
              <tbody>
                {currentProducts.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div className="product-row-left">
                        <div className="product-img-box">
                          <img
                            src={getProductImageUrl(p.image_url)}
                            alt={p.name}
                            onError={(e) => { e.target.src = getProductImageUrl('/uploads/panel_72led.jpg'); }}
                          />
                        </div>
                        <span className="product-item-title">{p.name}</span>
                      </div>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#334155' }}>{p.sku}</td>
                    <td style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>{p.stock} Units</td>
                    <td>
                      <span className={`badge-pill ${p.stock > 18 ? 'badge-completed' : 'badge-low-stock'}`}>
                        {p.stock > 18 ? 'Sufficient Stock' : 'Low Stock Warning'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="number"
                          min="0"
                          className="styled-input"
                          style={{ width: '80px', height: '36px', padding: '0 8px', textAlign: 'center', fontWeight: 700 }}
                          value={stockInputs[p.id] !== undefined ? stockInputs[p.id] : p.stock}
                          onChange={(e) => handleStockChange(p.id, e.target.value)}
                        />
                        <button
                          onClick={() => handleSaveStock(p.id)}
                          className="btn-red-submit"
                          style={{ width: 'auto', padding: '0 14px', height: '36px', fontSize: '12px' }}
                        >
                          Update Stock
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
                  Page {currentPage} of {totalPages}
                </span>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
                  >
                    <ChevronLeft size={16} />
                    <span>Previous</span>
                  </button>

                  {[...Array(totalPages)].map((_, idx) => (
                    <button
                      key={idx + 1}
                      onClick={() => setCurrentPage(idx + 1)}
                      style={{ width: '32px', height: '32px', border: '1px solid', borderColor: currentPage === idx + 1 ? '#dc2626' : '#e2e8f0', borderRadius: '6px', background: currentPage === idx + 1 ? '#dc2626' : '#ffffff', color: currentPage === idx + 1 ? '#ffffff' : '#334155', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                    >
                      {idx + 1}
                    </button>
                  ))}

                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
                  >
                    <span>Next</span>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Inventory;
